import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import { getSelectedStoreTenant } from '../selectors/tenant.selectors';


import * as TimeSlotsActions from '../actions/timeslots.actions';
import { getSelectionDialogId, getTimeSlotDialogId, getTimeSlotsTableState } from '../selectors/timeslots.selectors';
import { LaravelTimeSlotsService } from '../../commons/services/laravel/laravel-time-slots.service';
import { NewTimeSlotsComponent } from '../../modules/home/modules/timeslots/components/new-time-slots/new-time-slots.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class TimeSlotsEffects {
    error$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(
                TimeSlotsActions.deleteTimeSlotFailed,
                TimeSlotsActions.saveTimeSlotFailed
            ),
            tap(({ error }) => {
              if (error) {
                this.alertService.showErrorMessage('Errore', error.message);
              }
            })
          ),
        { dispatch: false }
    );

    loadTimeSlots$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.loadTimeSlots),
          withLatestFrom(this.store$.select(getSelectedStoreTenant)),
          map(([{ page, perPage, order, direction, filters, includes}, tenant]) => {
            const filter = {...filters};
            if(filter.onlyCurrentTenant){
              filter.tenant_id = tenant.id
            }
            return {page, perPage, order, direction, filters: filter, includes};
          }),
          switchMap(({page, perPage, order, direction, filters, includes}) => {
              return this.timeSlotService
              .list(page, perPage, order, direction, filters, includes)
              .pipe(
                  map((result) => {
                      return TimeSlotsActions.loadTimeSlotsCompleted({
                        timeslots: result.data,
                        currentPage: page,
                        total: result.total,
                        perPage,
                        order,
                        direction,
                        filters,
                        includes
                    });
                  }),
                    catchError((error) =>
                      of(TimeSlotsActions.loadTimeSlotsFailed({ error })))
                );

          })
        )
    );
              
                
    changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.changePage),
          withLatestFrom(this.store$.select(getTimeSlotsTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                TimeSlotsActions.loadTimeSlots({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    changeSort = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.changeSort),
          withLatestFrom(this.store$.select(getTimeSlotsTableState)),
          map(
            ([
              action,
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                TimeSlotsActions.loadTimeSlots({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
              })
          )
        )
    );
    
    changeFilters = createEffect(() =>
      this.actions$.pipe(
        ofType(TimeSlotsActions.changeFilters),
        withLatestFrom(this.store$.select(getTimeSlotsTableState)),
        map(([{ filters }, { total, currentPage, perPage, direction, order, includes }]) => {
          
          const updatedFilters = {
            ...filters,
            // Se active è null, lo rimuoviamo
            active: filters.active === null ? undefined : filters.active
          };
    
          return TimeSlotsActions.loadTimeSlots({
            page: currentPage,
            perPage: perPage,
            order,
            direction,
            filters: updatedFilters,
            includes
          });
        })
      )
    );

    editTimeSlot$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TimeSlotsActions.editTimeSlot),
        map(({ timeslot }) => {
          let dialogRef = this.dialog.open(NewTimeSlotsComponent, {
            data: {
              timeslot,
            },
            panelClass: 'full-screen-dialog',
          });
          return TimeSlotsActions.timeSlotDialogOpened({
            dialogId: dialogRef.id,
          });
        })
      )
    );

    saveTimeSlot$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TimeSlotsActions.saveTimeSlot),
        withLatestFrom(this.store$.select(getSelectedStoreTenant)),
        mergeMap(([{ timeslots }, selectedTenant]) => {
          let tenantId = selectedTenant ? selectedTenant.id : null;
          let timeslotSave = {
            ...timeslots.toDTO(),
            tenant_id: tenantId
          };
          return this.timeSlotService.upsert(timeslotSave).pipe(
              map((result) =>
                TimeSlotsActions.saveTimeSlotCompleted({ timeslot: result })
              ),
              catchError((error) =>
              of(TimeSlotsActions.saveTimeSlotFailed({ error }))
            )
          );
        })
        )
    );
    
  
    onSaveCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.saveTimeSlotCompleted),
          map((action) => action.timeslot),
          tap((timeSlot) =>
            this.alertService.showConfirmMessage(
              `Orario di apertura con id ${timeSlot.id} salvato con successo`
            )
          ),
          map(() => TimeSlotsActions.closeTimeSlotsDialog())
        )
    );

    deleteTimeSlot$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.deleteTimeSlot),
          switchMap(({ timeslot }) =>
            this.alertService
              .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare l'elemento selezionato?`
              )
              .pipe(
                mergeMap((confirm) => {
                  return confirm
                    ? this.timeSlotService.delete(timeslot.id).pipe(
                        map(() =>
                            TimeSlotsActions.deleteTimeSlotCompleted({
                              timeslot,
                          })
                        ),
                        catchError((error) =>
                          of(TimeSlotsActions.deleteTimeSlotFailed({ error }))
                        )
                      )
                    : of(TimeSlotsActions.deleteTimeSlotCancelled());
                })
              )
          )
        )
    );

    onDeleteCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.deleteTimeSlotCompleted),
          tap(({ timeslot }) =>
            this.alertService.showConfirmMessage(
              `Orario di apertura con id ${timeslot.id} rimosso con successo`
            )
          ),
          map(() => TimeSlotsActions.closeTimeSlotsDialog())
        )
    );

    // closeDialog = createEffect(
    //     () =>
    //       this.actions$.pipe(
    //         ofType(TimeSlotsActions.closeTimeSlotsDialog),
    //         withLatestFrom(this.store$.select(getTimeSlotDialogId)),
    //         tap(([_, dialogId]) => {
    //           if (dialogId) {
    //             this.dialog.getDialogById(dialogId).close();
    //           }
    //         })
    //       ),
    //     { dispatch: false }
    // );

    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.saveTimeSlotCompleted),
          withLatestFrom(this.store$.select(getTimeSlotsTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                TimeSlotsActions.loadTimeSlots({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    reloadAfterDelete = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.deleteTimeSlotCompleted),
          withLatestFrom(this.store$.select(getTimeSlotsTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                TimeSlotsActions.loadTimeSlots({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );
    
    
    closeSelectionDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(TimeSlotsActions.closeSelectionDialog),
            withLatestFrom(this.store$.select(getSelectionDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );


    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.requestCloseTimeSlotsDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });
      
            // Imposta il messaggio del dialog
            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
      
            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? TimeSlotsActions.confirmCloseTimeSlotsDialog()
                  : TimeSlotsActions.cancelCloseTimeSlotsDialog()
              )
            );
          })
        )
      );
      
      
      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.confirmCloseTimeSlotsDialog),
          map(() => TimeSlotsActions.closeTimeSlotsDialog())
        )
      );
      
      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(TimeSlotsActions.cancelCloseTimeSlotsDialog),
          map(() => {
            return { type: 'NO_ACTION' };
          })
        )
      );
      
      closeDialog$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(TimeSlotsActions.closeTimeSlotsDialog),
            withLatestFrom(this.store$.select(getTimeSlotDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
      );
        

    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private timeSlotService: LaravelTimeSlotsService,
        private dialog: MatDialog,
        private alertService: AlertService
    ) {}
    
}
